<script setup>
import { getBasket, getCheckoutBasket } from '#common/services/checkoutServices'
import { formatPrice } from '#common/utils/checkout'

defineProps({
  cart: {
    type: Object,
    required: true,
  },
  isCheckout: {
    type: Boolean,
    required: false,
    default: false,
  },
  styling: {
    type: Object,
    required: false,
    default() {
      return {
        border: {
          classes: [],
        },
        subtotal: {
          classes: ['total'],
        },
      }
    },
  },
  mobile: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const { $store, $axios } = useNuxtApp()
const metadata = useMetadata()
const checkoutSpinner = ref(false)
const processingPromoCode = ref(false)
const surchargeDescription =
  'This fee services our industry-leading weather logistics, the handling of all communications with UPS and FedEx if a shipment is lost or damaged, and our satisfaction guarantee.'
const promoZeroDiscountMessage =
  'Unfortunately, the items in your cart are not eligible for this promotion'
function toggleCart() {
  $store.dispatch('toggleCart')
  checkoutSpinner.value = false /* set to true for spinner */
}
function isNumeric(value) {
  return /^-?\d+\.?\d*$/.test(value)
}
async function removePromoCode(code) {
  if (processingPromoCode.value) {
    return
  }
  try {
    processingPromoCode.value = true
    await $axios.post('/api/basket/remove-voucher/', { code })
    let basket = {}
    if (isCheckout.value) {
      basket = await getCheckoutBasket($axios)
    } else {
      basket = await getBasket($axios)
    }
    await $store.dispatch('updateCart', basket)
  } finally {
    processingPromoCode.value = false
  }
}
</script>

<template>
  <div :class="styling.border.classes">
    <div :class="['row', ...styling.subtotal.classes]">
      <div class="columns small-8 key">Subtotal</div>
      <div v-if="isNumeric(cart.subtotal)" class="columns small-4 val">
        <Price :amt="Number(cart.subtotal)" />
      </div>
      <div v-else class="columns small-4 val">-</div>
    </div>
    <div
      v-if="
        cart.lines && cart.lines.length > 0 && cart.offer_discounts.length > 0
      "
      class="highlighted-total"
    >
      <div
        v-for="(discount, index) in cart.offer_discounts"
        :key="index"
        :class="[isCheckout ? 'row credit' : 'row-' + index]"
      >
        <div class="columns small-8 key">{{ discount.name }}</div>
        <div v-if="isNumeric(discount.amount)" class="columns small-4 val">
          -<Price :amt="Number(discount.amount)" />
        </div>
        <div v-else class="columns small-4 val">-</div>
      </div>
    </div>
    <div
      v-if="
        cart.lines && cart.lines.length > 0 && cart.voucher_discounts.length > 0
      "
      class="highlighted-total"
    >
      <div
        v-for="(discount, index) in cart.voucher_discounts"
        :key="index"
        :class="[isCheckout ? 'row credit' : 'row' + '-' + index]"
      >
        <div class="columns small-8 key">
          Promo: {{ discount.code }}
          <a href="#" @click.prevent="removePromoCode(discount.code)">
            (remove)
          </a>
        </div>
        <div v-if="isNumeric(discount.amount)" class="columns small-4 val">
          -<Price :amt="Number(discount.amount)" />
        </div>
        <div v-else class="columns small-4 val">-</div>
      </div>
    </div>
    <div v-else-if="cart.vouchers" class="highlighted-total">
      <div
        v-for="(voucher, index) in cart.vouchers"
        :key="index"
        :class="[isCheckout ? 'row credit' : 'row-' + index]"
      >
        <div class="columns small-8 key">
          Promo: {{ voucher.code }}
          <a href="#" @click.prevent="removePromoCode(voucher.code)">
            (remove)
          </a>
          <i
            v-tooltip="promoZeroDiscountMessage"
            class="icon-info has-tip tip-left radius"
            aria-haspopup="true"
          ></i>
        </div>
        <div class="columns small-4 val">--</div>
      </div>
    </div>
    <div v-if="cart.refund_credits_total > 0">
      <div
        v-for="(credit, index) in cart.applied_refund_credits"
        :key="credit.name + '-' + index.toString()"
        :class="[isCheckout ? 'row credit' : 'row' + '-' + index]"
      >
        <div class="columns small-8 key">
          {{ credit.name }}
        </div>
        <div v-if="isNumeric(credit.amount)" class="columns small-4 val">
          -<Price :amt="Number(credit.amount)" />
        </div>
        <div v-else class="columns small-4 val">-</div>
      </div>
    </div>
    <div v-if="cart.applied_promo_credit" class="highlighted-total">
      <div :class="[isCheckout ? 'row credit' : 'row-0']">
        <div class="columns small-8 key">
          {{ cart.applied_promo_credit.name }}
        </div>
        <div
          v-if="isNumeric(cart.applied_promo_credit.amount)"
          class="columns small-4 val"
        >
          -<Price :amt="Number(cart.applied_promo_credit.amount)" />
        </div>
        <div v-else class="columns small-4 val">-</div>
      </div>
    </div>
    <div
      v-if="
        isCheckout &&
        (cart.ice_pack_surcharge > 0 || cart.expected_ice_pack_surcharge > 0)
      "
      class="row"
    >
      <div class="columns small-8 key">
        <span :class="{ leaf: metadata.constance.TCP_ECO === 'True' }">
          {{ metadata.constance.TCP_SHORTNAME }}
        </span>
      </div>
      <div
        v-if="isNumeric(cart.ice_pack_surcharge)"
        class="columns small-4 val"
      >
        <Price
          v-if="cart.ice_pack_surcharge > 0"
          :amt="Number(cart.ice_pack_surcharge)"
        />
        <Price
          v-else-if="cart.expected_ice_pack_surcharge > 0"
          class="expected-shipping"
          :amt="Number(cart.expected_ice_pack_surcharge)"
        />
      </div>
      <div v-else class="columns small-4 val">-</div>
    </div>
    <div v-if="isCheckout" class="row">
      <div class="columns small-8 key">
        {{ cart.shipping_method || 'Shipping' }}
      </div>
      <div
        v-if="
          !isNumeric(cart.shipping_price) &&
          !isNumeric(cart.expected_shipping_cost)
        "
        class="columns small-4 val"
      >
        -
      </div>
      <div
        v-else-if="
          cart.shipping_price > 0 &&
          cart.expected_shipping_cost > 0 &&
          formatPrice(cart.shipping_price) !==
            formatPrice(cart.expected_shipping_cost)
        "
        class="columns small-4 val"
      >
        <Price
          v-if="cart.expected_shipping_cost > 0"
          class="expected-shipping"
          :amt="Number(cart.expected_shipping_cost)"
        />
        <Price class="other-shipping" :amt="Number(cart.shipping_price)" />
      </div>
      <div v-else class="columns small-4 val">
        <Price
          v-if="cart.shipping_price > 0"
          :amt="Number(cart.shipping_price)"
        />
        <Price
          v-else-if="cart.expected_shipping_cost > 0"
          class="expected-shipping"
          :amt="Number(cart.expected_shipping_cost)"
        />
        <span v-else>Included</span>
      </div>
    </div>
    <div v-if="isCheckout && cart.service_fee_surcharge > 0" class="row">
      <div class="columns small-8 key">
        Service Fee
        <i
          id="service-fee-tip"
          v-tooltip="surchargeDescription"
          class="icon-info has-tip"
          aria-haspopup="true"
          :title="surchargeDescription"
        ></i>
      </div>
      <div
        v-if="isNumeric(cart.service_fee_surcharge)"
        class="columns small-4 val"
      >
        <Price :amt="Number(cart.service_fee_surcharge)" />
      </div>
      <div v-else class="columns small-4 val">-</div>
    </div>
    <div v-if="isCheckout" class="row">
      <div class="columns small-8 key">Estimated Tax</div>
      <div v-if="isNumeric(cart.total_taxes)" class="columns small-4 val">
        <Price :amt="Number(cart.total_taxes)" />
      </div>
      <div v-else class="columns small-4 val">-</div>
    </div>

    <div v-if="mobile" class="mobile-place-order-landmark"></div>

    <div v-if="!isCheckout" class="row">
      <div class="columns buttons">
        <nuxt-link
          id="id-cart-checkout"
          to="/checkout/"
          class="button button-solid max goto-checkout"
          @click="toggleCart"
          ><span v-if="!checkoutSpinner">Checkout</span>
          <Spinner v-if="checkoutSpinner" />
        </nuxt-link>
      </div>
    </div>
  </div>
</template>
